// The Peacock palette — the colours an entity may choose its branding from.
// Confirmed by Val, 2026-07-15. Re-anchored 2026-07-29 (primary accent #03B7C9).
// Mirrors the :root block in src/styles/global.css; that file styles, this one is what a human picks from. Keep them in step.
//
// WHY A LIST AND NOT A COLOUR WHEEL:
// the brand picker was an <input type="color"> — a free wheel with no relationship to
// the palette. That is where #e65100 and #d3016c came from: values chosen against a
// settings panel that could not show their effect (the CRM hardcoded its colours and
// ignored the brand entirely), so nothing picked ever looked like anything. A wheel
// also means every tenant can pick a colour that fights the fixed gradient rule and the
// semantics. The palette is the menu; the wheel stays available underneath for the case
// a real brand needs its exact hex.
//
// THE MODEL: "each entity chooses its own branding and the switcher determines what's
// displayed" (Val). Only the ACCENT is per-entity. The gradient rule, the canvas and the
// semantics are fixed platform signatures — see global.css.

export const PEACOCK_ACCENTS = [
    { name: 'Peacock Teal', hex: '#03B7C9', note: 'default' },
    { name: 'Cyan',         hex: '#22D3EE' },
    { name: 'Blue',         hex: '#3B82F6' },
    { name: 'Violet',       hex: '#8B5CF6' },
    { name: 'Magenta',      hex: '#EC4899' },
    { name: 'Gold',         hex: '#FBBF24' },
    { name: 'Green',        hex: '#34D399' },
    { name: 'Red',          hex: '#F87171' },
    { name: 'Slate',        hex: '#94A3B8' },
];

export const PALETTE_ROLE_DEFS = [
    { key: 'canvas_light', name: 'Canvas', description: 'Page background (light)', defaultHex: '#F4F6FA', paired: true, mode: 'light' },
    { key: 'canvas_dark', name: 'Canvas', description: 'Page background (dark)', defaultHex: '#0A0E14', paired: true, mode: 'dark' },
    { key: 'surface_light', name: 'Surface', description: 'Card / tile background (light)', defaultHex: '#FFFFFF', paired: true, mode: 'light' },
    { key: 'surface_dark', name: 'Surface', description: 'Card / tile background (dark)', defaultHex: '#0F1826', paired: true, mode: 'dark' },
    { key: 'nav_light', name: 'Nav', description: 'Sidebar / nav rail / panel background (light)', defaultHex: '#F4F6FA', paired: true, mode: 'light' },
    { key: 'nav_dark', name: 'Nav', description: 'Sidebar / nav rail / panel background (dark)', defaultHex: '#2D2D87', paired: true, mode: 'dark' },
    { key: 'primary', name: 'Primary', description: 'Brand / primary accent', defaultHex: '#03B7C9' },
    { key: 'secondary', name: 'Secondary', description: 'Support accent', defaultHex: '#0087A5' },
    { key: 'depth', name: 'Depth', description: 'Card depth / gradients', defaultHex: '#5A2D87' },
    { key: 'border', name: 'Border', description: 'Tile borders / muted text & icon color', defaultHex: '#8C8CA0' },
    { key: 'excellent', name: 'Excellent', description: 'Electric / excellent', defaultHex: '#187000' },
    { key: 'good', name: 'Good', description: 'Teal / good', defaultHex: '#0087A5' },
    { key: 'caution', name: 'Caution', description: 'Gold / caution', defaultHex: '#F1C61E' },
    { key: 'moderate', name: 'Moderate', description: 'Orange / moderate', defaultHex: '#FA8A2E' },
    { key: 'critical', name: 'Critical', description: 'True red / critical', defaultHex: '#FF0000' },
    { key: 'neutral', name: 'Neutral', description: 'Neutral / muted', defaultHex: '#94A3B8' },
];

export const DEFAULT_PALETTE = Object.fromEntries(
    PALETTE_ROLE_DEFS.map((role) => [role.key, role.defaultHex])
);

// The default accent when an entity has no explicit override set.
export const DEFAULT_ACCENT = DEFAULT_PALETTE.primary;

// The signature gradient rule — fixed for every tenant, shown for reference only.
export const RULE_STOPS = ['#EC4899', '#8B5CF6', '#3B82F6', '#22D3EE'];

export const isPaletteAccent = (hex) =>
    PEACOCK_ACCENTS.some((c) => c.hex.toLowerCase() === (hex || '').toLowerCase());
